/**
 * rendererRegistry.js
 * Maps datatype strings to AG Grid cellRenderer functions/components.
 * All renderers are React functional components.
 * Register new renderers here — no changes needed elsewhere.
 */

import React from 'react';

const getColMeta = (params) => params?.colMeta || {};

const CurrencyRenderer = (params) => {
  const { value } = params;
  const colMeta = getColMeta(params);

  if (value == null || value === '') return <span style={{ color: '#9ca3af' }}>—</span>;

  const num = Number(value);
  const currency = colMeta.currency || 'INR';
  const decimals = colMeta.decimals ?? 0;

  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    maximumFractionDigits: decimals,
  }).format(Math.abs(num));

  const isNeg = num < 0;
  return (
    <span style={{ color: isNeg ? '#dc2626' : 'inherit', fontVariantNumeric: 'tabular-nums' }}>
      {isNeg ? `(${formatted})` : formatted}
    </span>
  );
};

const PercentRenderer = (params) => {
  const { value } = params;
  const colMeta = getColMeta(params);
  if (value == null) return <span style={{ color: '#9ca3af' }}>—</span>;

  const pct = Number(value);
  const decimals = colMeta.decimals ?? 1;
  const capped   = Math.min(Math.max(pct, 0), 100);

  const barColor =
    pct > 90 ? '#dc2626' :
    pct > 70 ? '#f59e0b' :
    '#16a34a';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, height: '100%' }}>
      <div style={{ flex: 1, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${capped}%`, height: '100%', background: barColor, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', minWidth: 38, textAlign: 'right', color: barColor }}>
        {pct.toFixed(decimals)}%
      </span>
    </div>
  );
};

const DateRenderer = (params) => {
  const { value } = params;
  if (!value) return <span style={{ color: '#9ca3af' }}>—</span>;

  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return <span style={{ color: '#9ca3af' }}>{value}</span>;
    return <span>{d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>;
  } catch {
    return <span>{value}</span>;
  }
};

const BadgeRenderer = (params) => {
  const { value } = params;
  const colMeta = getColMeta(params);
  const badgeConfig = colMeta?.badgeConfig || {};
  const cfg = badgeConfig[value] || { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' };

  if (!value) return <span style={{ color: '#9ca3af' }}>—</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600,
        background: cfg.bg, color: cfg.text, whiteSpace: 'nowrap',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
        {value}
      </span>
    </div>
  );
};

const BooleanRenderer = (params) => {
  const val = params.value;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div style={{
        width: 16, height: 16, borderRadius: 4, border: `2px solid ${val ? '#1a56db' : '#d1d5db'}`,
        background: val ? '#1a56db' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {val && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
    </div>
  );
};

const ProgressRenderer = (params) => {
  const { value } = params;
  const colMeta = getColMeta(params);
  const max = colMeta.maxValue || 100;
  const pct = Math.min(Math.max((Number(value) / max) * 100, 0), 100);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%', padding: '0 4px' }}>
      <div style={{ flex: 1, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: '#1a56db', borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 11, minWidth: 30, textAlign: 'right' }}>{value}</span>
    </div>
  );
};

const FormulaRenderer = (params) => {
  const { value } = params;
  if (value == null) return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 9, color: '#6366f1', fontWeight: 700, lineHeight: 1 }}>ƒx</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
};

export const rendererRegistry = {
  currency: CurrencyRenderer,
  percent:  PercentRenderer,
  date:     DateRenderer,
  badge:    BadgeRenderer,
  boolean:  BooleanRenderer,
  progress: ProgressRenderer,
  formula:  FormulaRenderer,
};

export function getRenderer(datatype) {
  return rendererRegistry[datatype] || undefined;
}

export function registerRenderer(datatype, component) {
  rendererRegistry[datatype] = component;
}

export {
  CurrencyRenderer, PercentRenderer, DateRenderer,
  BadgeRenderer, BooleanRenderer, ProgressRenderer, FormulaRenderer,
};
